import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/api";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldCheck, LayoutDashboard, Users, ArrowDownToLine, Receipt, Settings as SettingsIcon,
  LogOut, Coins, Wallet, Check, X, RefreshCw, Snowflake, KeyRound, Banknote, ArrowUpRight,
} from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const dt = (s) => (s ? new Date(s).toLocaleString() : "-");

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "deposits", label: "Deposits", icon: Wallet },
  { id: "treasury", label: "Treasury", icon: Banknote },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
  { id: "ledger", label: "Ledger", icon: Receipt },
  { id: "settings", label: "Rules & Plans", icon: SettingsIcon },
];

function Stat({ label, value, accent = "emerald", testid }) {
  return (
    <div className="card-surface rounded-2xl p-5" data-testid={testid}>
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-extrabold font-mono mt-2 text-${accent}-400`}>{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [txInputs, setTxInputs] = useState({});
  const [manual, setManual] = useState({ user_id: "", amount: "", network: "TRC20" });
  const [cpOpen, setCpOpen] = useState(false);
  const [treasury, setTreasury] = useState(null);
  const [sweeping, setSweeping] = useState("");

  const loadTab = async (t) => {
    try {
      if (t === "overview") setOv((await api.get("/admin/overview")).data);
      if (t === "users") setUsers((await api.get("/admin/users")).data.users);
      if (t === "deposits") {
        setDeposits((await api.get("/admin/deposits")).data.deposits);
        setUsers((await api.get("/admin/users")).data.users);
      }
      if (t === "withdrawals") setWithdrawals((await api.get("/admin/withdrawals")).data.withdrawals);
      if (t === "treasury") { setTreasury(null); setTreasury((await api.get("/admin/treasury")).data); }
      if (t === "ledger") setLedger((await api.get("/admin/ledger")).data.transactions);
      if (t === "settings") {
        setSettings((await api.get("/admin/settings")).data.settings);
        setPlans((await api.get("/admin/plans")).data.plans);
      }
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { loadTab(tab); }, [tab]);

  const approve = async (w) => {
    const tx = txInputs[w.id];
    if (!tx) return toast.error("Enter the TXID (the hash you sent the USDT with)");
    try {
      await api.post(`/admin/withdrawals/${w.id}/approve`, { tx_hash: tx });
      toast.success("Withdrawal approved");
      loadTab("withdrawals");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reject = async (w) => {
    try {
      await api.post(`/admin/withdrawals/${w.id}/reject`, { reason: txInputs[w.id] || "Rejected by admin" });
      toast.success("Withdrawal rejected & refunded");
      loadTab("withdrawals");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const freeze = async (u) => {
    await api.post(`/admin/users/${u.id}/freeze`);
    toast.success("User updated");
    loadTab("users");
  };
  const runAccrual = async () => {
    await api.post("/admin/run-accrual");
    toast.success("Daily ROI accrual run complete");
    loadTab("overview");
  };
  const creditDeposit = async () => {
    if (!manual.user_id || !manual.amount) return toast.error("Select a user and enter an amount");
    try {
      await api.post("/admin/credit-deposit", { ...manual, amount: parseFloat(manual.amount) });
      toast.success("Deposit credited (referral + ledger updated)");
      setManual({ user_id: "", amount: "", network: "TRC20" });
      loadTab("deposits");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const sweep = async (network) => {
    const gas = network === "TRC20" ? "TRX" : "BNB";
    if (!window.confirm(`Sweep all ${network} USDT to your main wallet?\n\nEach deposit address needs ${gas} for gas (or a gas wallet must be configured), otherwise those addresses will be skipped with an error.`)) return;
    setSweeping(network);
    try {
      const { data } = await api.post("/admin/treasury/sweep", { network });
      toast.success(`Sweep complete: ${data.swept} address(es) swept`);
      const errs = data.results.filter((r) => r.status === "error");
      if (errs.length) toast.error(`${errs.length} failed: ${errs[0].reason}`);
      loadTab("treasury");
    } catch (e) { toast.error(apiErr(e)); } finally { setSweeping(""); }
  };
  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Business rules saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const savePlan = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id}`, p);
      toast.success(`${p.name} plan saved`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <ChangePasswordDialog open={cpOpen} onClose={() => setCpOpen(false)} />
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-[#0b0f16] p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-8">
          <ShieldCheck className="h-7 w-7 text-amber-400" />
          <span className="text-lg font-extrabold">Admin</span>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${tab === t.id ? "bg-amber-500/15 text-amber-400" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-white/5 mt-2">
            <Coins className="h-4 w-4" /> User View
          </button>
        </nav>
        <button data-testid="admin-change-password-btn" onClick={() => setCpOpen(true)}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 mt-4">
          <KeyRound className="h-4 w-4" /> Change Password
        </button>
        <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/login"); }}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 mt-3">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="flex items-center justify-between p-4 border-b border-border bg-[#0b0f16]">
          <div className="flex md:hidden gap-2 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tab === t.id ? "bg-amber-500 text-white" : "bg-white/5 text-slate-400"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <span className="hidden md:block text-sm text-slate-400">{user?.email}</span>
          <div className="flex md:hidden items-center gap-4">
            <button data-testid="admin-change-password-btn-mobile" onClick={() => setCpOpen(true)}>
              <KeyRound className="h-5 w-5 text-slate-400" />
            </button>
            <button data-testid="admin-logout-btn-mobile" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-5 w-5 text-slate-400" />
            </button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
          {tab === "overview" && ov && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Platform Overview</h1>
                <Button data-testid="admin-run-accrual-btn" onClick={runAccrual} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  <RefreshCw className="h-4 w-4 mr-2" /> Run Daily ROI
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat testid="admin-stat-deposited" label="Total Deposited" value={`$${fmt(ov.total_deposited)}`} />
                <Stat testid="admin-stat-invested" label="Active Staked" value={`$${fmt(ov.total_invested)}`} accent="amber" />
                <Stat testid="admin-stat-income" label="ROI Paid" value={`$${fmt(ov.total_income_paid)}`} accent="sky" />
                <Stat testid="admin-stat-referral" label="Referral Paid" value={`$${fmt(ov.total_referral_paid)}`} accent="purple" />
                <Stat testid="admin-stat-withdrawn" label="Total Withdrawn" value={`$${fmt(ov.total_withdrawn)}`} accent="red" />
                <Stat testid="admin-stat-available" label="User Available" value={`$${fmt(ov.total_available)}`} />
                <Stat testid="admin-stat-users" label="Total Users" value={ov.users} accent="sky" />
                <Stat testid="admin-stat-pending" label="Pending Withdrawals" value={ov.pending_withdrawals} accent="amber" />
              </div>
            </div>
          )}

          {tab === "users" && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Users</h1>
              <div className="card-surface rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-slate-400 border-b border-border">
                    <tr><th className="p-3">User</th><th className="p-3">Available</th><th className="p-3">Invested</th><th className="p-3">Referral</th><th className="p-3">Actions</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                        <td className="p-3"><div>{u.name}</div><div className="text-xs text-slate-500">{u.email}</div><div className="text-xs font-mono text-emerald-400">{u.referral_code}</div></td>
                        <td className="p-3 font-mono">${fmt(u.balance.available)}</td>
                        <td className="p-3 font-mono">${fmt(u.balance.invested)}</td>
                        <td className="p-3 font-mono">${fmt(u.balance.total_referral)}</td>
                        <td className="p-3">
                          {u.role !== "admin" && (
                            <Button data-testid={`admin-freeze-${u.id}`} onClick={() => freeze(u)} size="sm" variant="outline"
                              className={`rounded-full border-slate-700 ${u.frozen ? "text-emerald-400" : "text-red-400"}`}>
                              <Snowflake className="h-3 w-3 mr-1" /> {u.frozen ? "Unfreeze" : "Freeze"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "deposits" && (
            <div>
              <h1 className="text-2xl font-bold mb-6">On-chain Deposits</h1>
              <div className="card-surface rounded-2xl p-5 mb-6">
                <h3 className="font-semibold mb-1">Manual Credit <span className="text-xs text-slate-500">(blockchain-delay override)</span></h3>
                <p className="text-xs text-slate-500 mb-4">Manually credit a verified deposit — balance + referral + ledger update automatically.</p>
                <div className="grid sm:grid-cols-4 gap-3 items-end">
                  <div className="sm:col-span-2">
                    <label className="text-xs text-slate-400">User</label>
                    <select data-testid="admin-manual-user" value={manual.user_id}
                      onChange={(e) => setManual({ ...manual, user_id: e.target.value })}
                      className="mt-1 w-full bg-white/5 border border-slate-700 rounded-md px-3 py-2 text-sm">
                      <option value="">Select user…</option>
                      {users.filter((u) => u.role !== "admin").map((u) => (
                        <option key={u.id} value={u.id}>{u.email}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Amount</label>
                    <Input data-testid="admin-manual-amount" type="number" value={manual.amount}
                      onChange={(e) => setManual({ ...manual, amount: e.target.value })}
                      className="mt-1 bg-white/5 border-slate-700" />
                  </div>
                  <Button data-testid="admin-manual-credit-btn" onClick={creditDeposit}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">Credit</Button>
                </div>
              </div>
              <div className="card-surface rounded-2xl overflow-x-auto">
                {deposits.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="admin-no-deposits">No on-chain deposits verified yet.</div> : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-slate-400 border-b border-border"><tr><th className="p-3">User</th><th className="p-3">Network</th><th className="p-3">Amount</th><th className="p-3">TXID</th><th className="p-3">Time</th></tr></thead>
                    <tbody className="divide-y divide-border">
                      {deposits.map((d) => (
                        <tr key={d.tx_hash}><td className="p-3">{d.user_email}</td><td className="p-3">{d.network}</td>
                          <td className="p-3 font-mono text-emerald-400">${fmt(d.amount)}</td>
                          <td className="p-3 font-mono text-xs break-all max-w-[200px]">{d.tx_hash}</td>
                          <td className="p-3 text-xs text-slate-500">{dt(d.created_at)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {tab === "withdrawals" && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Withdrawal Approvals</h1>
              <div className="space-y-3">
                {withdrawals.length === 0 ? <div className="card-surface rounded-2xl p-8 text-center text-slate-500" data-testid="admin-no-withdrawals">No withdrawal requests.</div> :
                  withdrawals.map((w) => (
                    <div key={w.id} className="card-surface rounded-2xl p-4" data-testid={`admin-withdrawal-${w.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div>
                          <span className="font-mono font-semibold text-lg">${fmt(w.amount)}</span>
                          <span className="text-xs text-slate-500 ml-2">{w.network} · fee ${fmt(w.fee)} · net ${fmt(w.net_amount)}</span>
                          <div className="text-xs text-slate-500">{w.user_email}</div>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full ${w.status === "pending" ? "bg-amber-500/10 text-amber-400" : w.status === "approved" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>{w.status}</span>
                      </div>
                      <div className="text-xs text-slate-400 break-all mb-3">To: {w.to_address}</div>
                      {w.status === "pending" ? (
                        <div className="flex flex-wrap gap-2 items-center">
                          <Input data-testid={`admin-txid-input-${w.id}`} placeholder="Paste TXID after sending USDT"
                            value={txInputs[w.id] || ""} onChange={(e) => setTxInputs({ ...txInputs, [w.id]: e.target.value })}
                            className="flex-1 min-w-[200px] bg-white/5 border-slate-700 font-mono text-xs" />
                          <Button data-testid={`admin-approve-withdrawal-${w.id}`} onClick={() => approve(w)} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button data-testid={`admin-reject-withdrawal-${w.id}`} onClick={() => reject(w)} variant="outline" className="rounded-full border-red-500/30 text-red-400">
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <div className="text-xs">{w.tx_hash && <span className="text-emerald-400 break-all">TXID: {w.tx_hash}</span>}{w.reason && <span className="text-red-400">Reason: {w.reason}</span>}</div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {tab === "ledger" && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Full Ledger</h1>
              <div className="card-surface rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-slate-400 border-b border-border"><tr><th className="p-3">User</th><th className="p-3">Type</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Time</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {ledger.map((r) => (
                      <tr key={r.id}><td className="p-3 text-xs">{r.user_email}</td><td className="p-3 capitalize">{r.type.replace("_", " ")}</td>
                        <td className="p-3 font-mono">${fmt(r.amount)}</td>
                        <td className="p-3"><span className={`text-xs ${r.status === "completed" ? "text-emerald-400" : r.status === "rejected" ? "text-red-400" : "text-amber-400"}`}>{r.status}</span></td>
                        <td className="p-3 text-xs text-slate-500">{dt(r.created_at)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "settings" && settings && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Business Rules & Plans</h1>
              <div className="card-surface rounded-2xl p-6 mb-6">
                <h3 className="font-semibold mb-4">Limits & Fees</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[["min_deposit", "Min Deposit"], ["min_withdrawal", "Min Withdrawal"], ["max_withdrawal", "Max Withdrawal"], ["withdrawal_fee_pct", "Withdrawal Fee %"]].map(([k, l]) => (
                    <div key={k}>
                      <label className="text-xs text-slate-400">{l}</label>
                      <Input data-testid={`admin-setting-${k}`} type="number" value={settings[k] ?? ""}
                        onChange={(e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) })}
                        className="mt-1 bg-white/5 border-slate-700" />
                    </div>
                  ))}
                </div>
                <h3 className="font-semibold mt-6 mb-3">Referral Levels (%)</h3>
                <div className="grid grid-cols-3 gap-3">
                  {(settings.referral_levels || []).map((lvl, i) => (
                    <div key={i}>
                      <label className="text-xs text-slate-400">Level {lvl.level}</label>
                      <Input data-testid={`admin-referral-level-${lvl.level}`} type="number" value={lvl.pct}
                        onChange={(e) => {
                          const next = [...settings.referral_levels];
                          next[i] = { ...lvl, pct: parseFloat(e.target.value) };
                          setSettings({ ...settings, referral_levels: next });
                        }}
                        className="mt-1 bg-white/5 border-slate-700" />
                    </div>
                  ))}
                </div>
                <h3 className="font-semibold mt-6 mb-3">Main / Collection Wallets</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-400">Main Wallet (TRC20)</label>
                    <Input data-testid="admin-main-wallet-trc20" value={settings.main_wallet_trc20 || ""}
                      onChange={(e) => setSettings({ ...settings, main_wallet_trc20: e.target.value })}
                      className="mt-1 bg-white/5 border-slate-700 font-mono text-xs" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Main Wallet (BEP20)</label>
                    <Input data-testid="admin-main-wallet-bep20" value={settings.main_wallet_bep20 || ""}
                      onChange={(e) => setSettings({ ...settings, main_wallet_bep20: e.target.value })}
                      className="mt-1 bg-white/5 border-slate-700 font-mono text-xs" />
                  </div>
                </div>
                <Button data-testid="admin-save-settings-btn" onClick={saveSettings} className="mt-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  Save Rules
                </Button>
              </div>

              <div className="space-y-4">
                {plans.map((p, idx) => (
                  <div key={p.id} className="card-surface rounded-2xl p-5" data-testid={`admin-plan-${p.id}`}>
                    <div className="font-semibold mb-3">{p.name} <span className="text-xs text-slate-500 font-mono">({p.id})</span></div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[["daily_roi_pct", "Daily ROI %"], ["duration_days", "Duration (days)"], ["min_amount", "Min USDT"], ["max_amount", "Max USDT"]].map(([k, l]) => (
                        <div key={k}>
                          <label className="text-xs text-slate-400">{l}</label>
                          <Input data-testid={`admin-plan-${p.id}-${k}`} type="number" value={p[k]}
                            onChange={(e) => { const next = [...plans]; next[idx] = { ...p, [k]: parseFloat(e.target.value) }; setPlans(next); }}
                            className="mt-1 bg-white/5 border-slate-700" />
                        </div>
                      ))}
                    </div>
                    <Button data-testid={`admin-save-plan-${p.id}`} onClick={() => savePlan(plans[idx])} size="sm" className="mt-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                      Save {p.name}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
